import { useTranslation } from "react-i18next";
import { X, Download, ZoomIn, ZoomOut, RotateCw } from "lucide-react";
import { Button } from "@stockline/ui/components/button";

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const SCALE_STEP = 0.25;

/**
 * Shared zoom/rotation/download/close toolbar for the image / html / text
 * preview branches. Each callback is optional so a branch can opt out of the
 * affordances it doesn't support (e.g. text has no rotation).
 */
export function ZoomControls({
  scale,
  onScaleChange,
  onReset,
  rotation,
  onRotationChange,
  onClose,
  onDownload,
  isDownloading,
}: Readonly<{
  scale: number;
  onScaleChange: (s: number) => void;
  onReset: () => void;
  rotation?: number;
  onRotationChange?: (r: number) => void;
  onClose?: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
}>) {
  const { t } = useTranslation();
  return (
    <div className="bg-white border-b p-2 flex items-center justify-start gap-2 flex-shrink-0">
      {onDownload && (
        <Button
          size="sm"
          variant="outline"
          onClick={onDownload}
          disabled={isDownloading}
          title={t(isDownloading ? "chat.downloading" : "chat.download")}
          aria-label={t(isDownloading ? "chat.downloading" : "chat.download")}
        >
          <Download className="h-4 w-4" />
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={() => onScaleChange(Math.max(scale - SCALE_STEP, MIN_SCALE))}
        disabled={scale <= MIN_SCALE}
      >
        <ZoomOut className="h-4 w-4" />
      </Button>
      <span className="text-sm min-w-[50px] text-center">
        {Math.round(scale * 100)}%
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onScaleChange(Math.min(scale + SCALE_STEP, MAX_SCALE))}
        disabled={scale >= MAX_SCALE}
      >
        <ZoomIn className="h-4 w-4" />
      </Button>
      <Button size="sm" variant="outline" onClick={onReset}>
        {t("common.reset", { defaultValue: "Reset" })}
      </Button>
      {onRotationChange && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onRotationChange(((rotation ?? 0) + 90) % 360)}
        >
          <RotateCw className="h-4 w-4" />
        </Button>
      )}
      {onClose && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="ml-auto"
          title={t("common.close", { defaultValue: "Close" })}
          aria-label={t("common.close", { defaultValue: "Close" })}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
